import { GetInSplitLayout } from './get-in-split-layout'
import { Button } from 'src/component/ui/button'
import { Input } from 'src/component/ui/input/input'
import { isFeatureFlagged } from '../../../../util/feature-flags'
import { FormFieldError } from 'src/component/ui/form-field-error'
import { OverlaySpinner } from '../../util/spinner/overlay-spinner'
import { useRequestPasswordlessLogin } from '../../../../hook/use-request-passwordless-login'
import { promoCodeService } from '../../../../service/promo-code-service'
import { PromoCodeNotice } from 'src/component/profile/promo-code-notice'
import { yupResolver } from '@hookform/resolvers/yup'
import { Turnstile } from '@marsidev/react-turnstile'
import { GoogleSignInButton } from './google-sign-in-button'
import { AppleSignInButton } from './apple-sign-in-button'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY as string | undefined

const getInEmailSchema = yup
  .object({
    email: yup.string().email('Please enter a valid email address').trim().required('Email is required.'),
    captchaToken: TURNSTILE_SITE_KEY
      ? yup.string().nullable().required('Please complete the captcha')
      : yup.string().nullable().optional(),
  })
  .required()

type FormValues = yup.InferType<typeof getInEmailSchema>

type PromoStatus = { valid: true; code: string; label: string | null } | { valid: false; code: string } | null

export const GetInEmailStep = (): React.ReactElement => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(getInEmailSchema),
    defaultValues: { email: '', captchaToken: null },
    mode: 'onTouched',
  })

  const { requestPasswordlessLogin, isLoading: isRequestPasswordlessLoading } = useRequestPasswordlessLogin({
    onError: () => setValue('captchaToken', null),
  })

  const [searchParams] = useSearchParams()
  const [promoStatus, setPromoStatus] = useState<PromoStatus>(null)

  useEffect(() => {
    const code = searchParams.get('promo')
    if (!code) return

    promoCodeService.validate(code).then(({ valid, discountLabel, appliesToProductIds }) => {
      if (valid) {
        promoCodeService.store({ code, discountLabel, appliesToProductIds })
        setPromoStatus({ valid: true, code, label: discountLabel })
      } else {
        setPromoStatus({ valid: false, code })
      }
    })
  }, [searchParams])

  const isCaptchaRequired = !!TURNSTILE_SITE_KEY
  const isSubmitDisabled = !isValid

  const onSubmit = useCallback(
    (data: FormValues): void => {
      requestPasswordlessLogin({
        email: data.email,
        captchaToken: data.captchaToken ?? undefined,
      })
    },
    [requestPasswordlessLogin],
  )

  return (
    <GetInSplitLayout>
      {/* The `lg:` clamps below are the sign-in column's share of the GH-908 vertical scale;
          `get-in-split-layout.tsx` explains the whole of it and why each ceiling is the value
          it replaces. */}
      <div className="relative flex-1 flex flex-col items-center justify-center w-full max-w-[541px] mx-auto">
        <div className="flex flex-col items-center gap-6 lg:gap-[clamp(20px,4dvh,36px)] w-full">
          <div className="flex flex-col items-center gap-6 lg:gap-[clamp(16px,2.6dvh,24px)] w-full">
            {!isFeatureFlagged('APPLE_SIGN_IN') && <AppleSignInButton disabled={isRequestPasswordlessLoading} />}
            {!isFeatureFlagged('GOOGLE_SIGN_IN') && <GoogleSignInButton disabled={isRequestPasswordlessLoading} />}
          </div>
          <p className="font-roboto-condensed text-[22px] leading-7 text-ivory">Or</p>
          <form className="flex flex-col gap-4 lg:gap-[clamp(10px,1.8dvh,16px)] w-full" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-2">
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    size="xl"
                    type="text"
                    placeholder="Your Email Address"
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    variant={errors.email ? 'error' : 'default'}
                    className="font-roboto-condensed lg:h-[clamp(60px,8.6dvh,78px)]"
                  />
                )}
              />
              <FormFieldError>{errors.email?.message}</FormFieldError>
              {promoStatus?.valid === true && (
                <PromoCodeNotice variant="applied" code={promoStatus.code} discountLabel={promoStatus.label} />
              )}
              {promoStatus?.valid === false && <PromoCodeNotice variant="invalid" code={promoStatus.code} />}
            </div>
            {isCaptchaRequired && (
              <Controller
                control={control}
                name="captchaToken"
                render={({ field: { onChange, onBlur } }) => (
                  <div className="flex justify-center">
                    <Turnstile
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => {
                        onChange(token)
                        onBlur()
                      }}
                      onExpire={() => onChange(null)}
                      onError={() => onChange(null)}
                      options={{ theme: 'dark' }}
                    />
                  </div>
                )}
              />
            )}
            <Button
              type="submit"
              className="w-full h-16 lg:h-[clamp(52px,7dvh,64px)] rounded-full text-2xl font-evogria uppercase"
              disabled={isSubmitDisabled || isRequestPasswordlessLoading}
            >
              GET IN
            </Button>
            <p className="font-roboto-condensed text-base leading-6 text-ivory tracking-[0.5px] text-center">
              By confirming, you agree to our{' '}
              <Link
                to="/auth/legal/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline font-roboto-condensed text-base"
              >
                Terms
              </Link>
              ,{' '}
              <Link
                to="/auth/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline font-roboto-condensed text-base"
              >
                Privacy Policy
              </Link>{' '}
              and sending Marketing & Promotional emails.
            </p>
          </form>
        </div>
        {isRequestPasswordlessLoading && <OverlaySpinner />}
      </div>
    </GetInSplitLayout>
  )
}
